import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { CheckCircle2, HelpCircle, Mail, Users, ArrowRight } from "lucide-react";
import { timeAgo } from "../lib/format";

const STATE_META = {
  matched: {
    label: "Matched",
    cls: "bg-emerald-50 text-emerald-900 border-emerald-300",
    Icon: CheckCircle2,
  },
  no_matching_company: {
    label: "No matching company",
    cls: "bg-amber-50 text-amber-900 border-amber-300",
    Icon: HelpCircle,
  },
  unmatched_email: {
    label: "Unmatched email",
    cls: "bg-red-50 text-red-900 border-red-300",
    Icon: Mail,
  },
};

const CHIPS = [
  { id: "all", label: "All" },
  { id: "matched", label: "Matched" },
  { id: "no_matching_company", label: "No matching company" },
  { id: "unmatched_email", label: "Unmatched email" },
];

export default function Customers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("state") || "all");

  const load = useCallback(async () => {
    const { data } = await api.get("/customers",
      { params: { state: filter === "all" ? undefined : filter } });
    setRows(data.items || []);
    setCounts(data.counts || {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setChip = (s) => {
    setFilter(s);
    const next = new URLSearchParams(searchParams);
    if (s === "all") next.delete("state");
    else next.set("state", s);
    setSearchParams(next, { replace: true });
  };

  const total = (counts.matched || 0) + (counts.no_matching_company || 0)
              + (counts.unmatched_email || 0);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-8 py-10">
        <PageHeader
          eyebrow="company setup"
          title="Customer list"
          description="Every customer you've billed. Lingua Franca matches by email — customers without a Lingua Franca account can't be sent Bills, and past invoices to them stay held (no auto-release when they later sign up)."
        />

        <div className="mt-8 flex items-center gap-2" data-testid="customers-filters">
          {CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${
                filter === c.id ? "border-black bg-black text-white" : "border-neutral-300 text-neutral-700"
              }`}
              onClick={() => setChip(c.id)}
              data-testid={`customers-filter-${c.id}`}
            >
              {c.label} · {c.id === "all" ? total : counts[c.id] || 0}
            </button>
          ))}
        </div>

        <div className="mt-6 border border-neutral-200" data-testid="customers-table">
          <div className="grid grid-cols-12 text-xs uppercase tracking-widest bg-neutral-50 border-b border-neutral-200 px-4 py-3">
            <div className="col-span-4">Customer</div>
            <div className="col-span-3">Email used for match</div>
            <div className="col-span-2">Match</div>
            <div className="col-span-1 text-right">Invoices</div>
            <div className="col-span-1 text-right">Stuck</div>
            <div className="col-span-1 text-right">Last seen</div>
          </div>
          {rows.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm" data-testid="customers-empty">
              <Users size={20} className="mx-auto mb-3 text-neutral-300" />
              <div className="text-neutral-700">No customers yet.</div>
              <div className="text-neutral-500 mt-1 text-xs">
                Customers appear here after you pull your first outbound invoice from a connected GL. Make sure you have at least one GL connected under Connections.
              </div>
            </div>
          ) : rows.map((r, idx) => {
            const meta = STATE_META[r.match_state] || STATE_META.unmatched_email;
            const key = `${r.customer_email || "noemail"}-${idx}`;
            return (
              <div key={key}
                   className="grid grid-cols-12 items-center px-4 py-3 border-b border-neutral-100 text-sm hover:bg-neutral-50"
                   data-testid={`customer-row-${idx}`}>
                <div className="col-span-4">
                  <div className="font-medium">{r.customer_name}</div>
                  {r.matched_company_name && (
                    <div className="text-xs text-neutral-500">
                      → {r.matched_company_name}
                    </div>
                  )}
                </div>
                <div className="col-span-3 font-mono text-xs text-neutral-600 truncate">
                  {r.customer_email || <span className="italic text-neutral-400">—</span>}
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs uppercase tracking-widest border ${meta.cls}`}
                        data-testid={`customer-match-${idx}`}>
                    <meta.Icon size={10} /> {meta.label}
                  </span>
                </div>
                <div className="col-span-1 text-right font-mono text-sm">
                  {r.invoice_count}
                </div>
                <div className="col-span-1 text-right">
                  {r.stuck_count > 0 ? (
                    <button
                      type="button"
                      className="text-xs underline text-amber-800 inline-flex items-center gap-1"
                      onClick={() => navigate(
                        `/invoices?customer=${encodeURIComponent(r.customer_name)}&sync_status=held`
                      )}
                      data-testid={`customer-stuck-${idx}`}
                    >
                      {r.stuck_count} <ArrowRight size={10} />
                    </button>
                  ) : (
                    <span className="text-xs text-neutral-400 font-mono">0</span>
                  )}
                </div>
                <div className="col-span-1 text-right text-xs text-neutral-500">
                  {timeAgo(r.last_seen_at)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-neutral-500">
          Unmatched customer? Ask them to register on Lingua Franca (or correct the email on your side, then re-issue the invoice from your GL) — Lingua Franca will not auto-post historical invoices, only ones you re-issue after they've signed up.
        </div>
      </div>
    </Layout>
  );
}
